import axios from 'axios';
import { Playbook } from '../types/playbook';
import env from '../utils/env';
import { getSupabaseClient } from '../config/supabase';
import { Json } from '../../backend/src/types/supabase'; // Import Json type from supabase.ts

const API_BASE_URL = `${env.apiUrl}/playbooks`;

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const supabase = getSupabaseClient();
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('Supabase getSession error in playbookApiService:', sessionError);
  }

  const token = session?.access_token;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('No Supabase session token found in playbookApiService. API requests might be unauthorized.');
  }
  return headers;
};

// This interface represents the structure of the playbook data as stored in the backend database.
// Fields like uiTransforms, uiEventConfigs, params, and autoPromoteGlobals are part of the 'content' JSONB field in the DB.
export interface PlaybookApiPayload {
  id?: string; // Optional for insert
  name: string;
  description?: string | null;
  type: string; // 'pipeline' | 'integration'
  version: string;
  content: Json; // This is where the core playbook content (mappings, filters, etc.) will be nested
  input_schema_id?: string | null;
  output_schema_id?: string | null;
  status: string; // 'saved'
  // These fields are now top-level in the backend payload, matching the frontend Playbook type
  params?: { name: string; exampleValue?: string }[];
  // uiTransforms, uiEventConfigs, and autoPromoteGlobals are now part of the content JSONB
  allowUndefinedTransforms?: boolean; // New setting
  connectionJSON?: Json | null; // Add connectionJSON to the API payload
}

// CreatePlaybookPayload and UpdatePlaybookPayload now use the frontend Playbook type
// The transformation to PlaybookApiPayload happens within the service functions.
export type CreatePlaybookPayload = Playbook;
export type UpdatePlaybookPayload = Partial<Playbook>;

export const createPlaybook = async (playbook: CreatePlaybookPayload): Promise<Playbook> => {
  const authHeaders = await getAuthHeaders();
  const backendPayload: PlaybookApiPayload = {
    name: playbook.name,
    description: playbook.description,
    type: playbook.type,
    version: playbook.version,
    input_schema_id: playbook.input_schema_id,
    output_schema_id: playbook.output_schema_id,
    status: playbook.status,
    content: playbook.content, // Core playbook content, now includes uiTransforms, uiEventConfigs, autoPromoteGlobals
    params: playbook.params, // Still top-level
    allowUndefinedTransforms: playbook.allowUndefinedTransforms, // Still top-level
    connectionJSON: playbook.connectionJSON as Json, // Cast connectionJSON to Json type
  };
  const response = await axios.post(API_BASE_URL, backendPayload, { headers: authHeaders });
  // Transform the response data back to the frontend Playbook type
  const backendData = response.data;
  // The backend response should now directly match the frontend Playbook type
  return backendData as Playbook;
};

export const getPlaybook = async (id: string): Promise<Playbook> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.get(`${API_BASE_URL}/${id}`, { headers: authHeaders });
  // Transform the response data back to the frontend Playbook type
  const backendData = response.data;
  // The backend response should now directly match the frontend Playbook type
  return backendData as Playbook;
};

export const updatePlaybook = async (id: string, playbookUpdates: UpdatePlaybookPayload): Promise<Playbook> => {
  const authHeaders = await getAuthHeaders();

  // Fetch the existing playbook to merge content correctly for partial updates
  // This ensures that fields not explicitly updated are preserved in the 'content' JSONB.
  const existingPlaybook = await getPlaybook(id);

  const backendPayload: Partial<PlaybookApiPayload> = {
    name: playbookUpdates.name,
    description: playbookUpdates.description,
    type: playbookUpdates.type,
    version: playbookUpdates.version,
    input_schema_id: playbookUpdates.input_schema_id !== undefined ? playbookUpdates.input_schema_id : existingPlaybook.input_schema_id,
    output_schema_id: playbookUpdates.output_schema_id !== undefined ? playbookUpdates.output_schema_id : existingPlaybook.output_schema_id,
    status: playbookUpdates.status,
    content: playbookUpdates.content !== undefined ? playbookUpdates.content : existingPlaybook.content, // Content now includes uiTransforms, uiEventConfigs, autoPromoteGlobals
    params: playbookUpdates.params !== undefined ? playbookUpdates.params : existingPlaybook.params,
    allowUndefinedTransforms: playbookUpdates.allowUndefinedTransforms !== undefined ? playbookUpdates.allowUndefinedTransforms : existingPlaybook.allowUndefinedTransforms,
    connectionJSON: playbookUpdates.connectionJSON !== undefined ? playbookUpdates.connectionJSON as Json : existingPlaybook.connectionJSON as Json, // Include connectionJSON with proper casting
  };

  const response = await axios.put(`${API_BASE_URL}/${id}`, backendPayload, { headers: authHeaders });
  // Transform the response data back to the frontend Playbook type
  const backendData = response.data;
  // The backend response should now directly match the frontend Playbook type
  return backendData as Playbook;
};

export const fetchAllPlaybooks = async (): Promise<Playbook[]> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.get(API_BASE_URL, { headers: authHeaders });
  return response.data;
};

export const deletePlaybook = async (id: string): Promise<void> => {
  const authHeaders = await getAuthHeaders();
  await axios.delete(`${API_BASE_URL}/${id}`, { headers: authHeaders });
};

export const duplicatePlaybook = async (id: string, newName: string): Promise<Playbook> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.post(`${API_BASE_URL}/${id}/duplicate`, { name: newName }, { headers: authHeaders });
  return response.data;
};

export const exportPlaybookYaml = async (id: string): Promise<string> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.get(`${API_BASE_URL}/${id}/export/yaml`, { 
    headers: authHeaders,
    responseType: 'text'
  });
  return response.data;
};

export const importPlaybookFromYaml = async (yamlContent: string, name: string): Promise<Playbook> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.post(`${API_BASE_URL}/import/yaml`, { 
    yamlContent, 
    name 
  }, { headers: authHeaders });
  return response.data;
};

export const validatePlaybook = async (playbook: Playbook): Promise<{ isValid: boolean; errors: string[] }> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.post(`${API_BASE_URL}/validate`, playbook, { headers: authHeaders });
  return response.data;
};

export const getPlaybooksBySchemaId = async (schemaId: string): Promise<Playbook[]> => {
  const authHeaders = await getAuthHeaders();
  const response = await axios.get(`${API_BASE_URL}/by-schema/${schemaId}`, { headers: authHeaders });
  return response.data;
};
